import { LabelLang } from "@ennova-research/waveup-common/enum";

/** La lingua con cui si parte quando non si sa che altro dire */
const FALLBACK: LabelLang = LabelLang.EN_US;

const SUPPORTED = Object.values(LabelLang);

/** `it-IT` → `it`, `IT` → `it`: la lingua senza il paese */
const subtagOf = (tag: string): string =>
  tag.trim().toLowerCase().split("-")[0];

/**
 * Con che lingua aprire l'applicazione.
 *
 * L'ordine è: quella già scelta dalla persona, poi quella del browser, poi
 * l'inglese. Il passaggio che merita una spiegazione è il secondo.
 *
 * Le lingue tradotte sono due, `it-IT` e `en-US`, ma i browser dichiarano il
 * tag intero: `it`, `it-CH`, `en-GB`. Confrontare il tag per intero faceva
 * cadere tutti questi sull'inglese — un'azienda italiana con il browser su
 * `it-CH` si trovava il modulo di registrazione in inglese, sulle pagine
 * pubbliche dove non c'è nemmeno un selettore di lingua con cui rimediare.
 *
 * Quindi se il tag intero non combacia si guarda la **sottolingua**: `it-*`
 * porta all'italiano, `en-*` all'inglese. Resta una scelta approssimata —
 * all'italiano della Svizzera serviranno le sue valute e le sue date, e qui
 * riceve quelle italiane — ma è l'approssimazione giusta: capire la pagina
 * viene prima di come è formattato un numero.
 *
 * @param stored la lingua già scelta, come l'ha scritta la persistenza di
 * Transloco; viene accettata solo se esatta, perché la scriviamo noi
 * @param browserTag quello che dichiara il browser, nella forma che arriva
 */
export function resolveLanguage(
  stored: string | null | undefined,
  browserTag: string | null | undefined
): LabelLang {
  if (stored && SUPPORTED.includes(stored as LabelLang)) {
    return stored as LabelLang;
  }

  if (!browserTag) {
    return FALLBACK;
  }

  const esatta = SUPPORTED.find(
    (lang) => lang.toLowerCase() === browserTag.trim().toLowerCase()
  );

  if (esatta) {
    return esatta;
  }

  const perSottolingua = SUPPORTED.find(
    (lang) => subtagOf(lang) === subtagOf(browserTag)
  );

  return perSottolingua ?? FALLBACK;
}
